import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { wasteProviderInfoService } from '../../../../services/wasteProviderInfo';
import { vaultService } from '../../../../services/vaultService';

import { createLogger } from '../../../../utils/logger';
import { useUserSelection } from '../../../../contexts/UserSelectionContext';
const logger = createLogger('WasteProviderData');

type RouteParams = {
  targetUserId?: string;
  viewOnly?: boolean;
};

type InfoItemProps = {
  label: string;
  value: string;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => (
  <View style={styles.infoItem}>
    <Text style={styles.label}>{label}</Text>
    <Text style={styles.value}>{value || 'Not specified'}</Text>
    <View style={styles.separator} />
  </View>
);

export const WasteProviderDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, RouteParams>, string>>();
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly || false;
  // K5: a successor sees the owner's logins once the death is verified.
  const { isDeathVerifiedForSelectedUser } = useUserSelection();
  const [wasteProviderData, setWasteProviderData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUsername, setShowUsername] = useState<{ [key: string]: boolean }>({});
  const [showPassword, setShowPassword] = useState<{ [key: string]: boolean }>({});
  const [showAccountNumber, setShowAccountNumber] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    loadWasteProviderData();
  }, []);

  const loadWasteProviderData = async () => {
    try {
      setLoading(true);
      const data = await wasteProviderInfoService.getAllWasteProviderInfo(targetUserId);
      
      if (data && data.length > 0) {
        // Skip loading portal credentials in viewOnly mode - they are protected until EOL
        if (!viewOnly || isDeathVerifiedForSelectedUser) {
          // Load portal credentials for each waste provider
          const updatedData = await Promise.all(
            data.map(async (wasteProvider) => {
              try {
                logger.debug('[Portal Debug] Attempting to load portal credentials for waste provider ID:', wasteProvider.id);
                const portalData = await vaultService.getPortalCredentials(wasteProvider.id, 'waste-provider');
                logger.debug('[Portal Debug] Portal data retrieved for waste provider:', wasteProvider.id, portalData);
                if (portalData) {
                  wasteProvider.username = portalData.username;
                  wasteProvider.password = portalData.password;
                  wasteProvider.accountNumber = portalData.accountNumber;
                  logger.debug('[Portal Debug] Assigned credentials to waste provider data');
                } else {
                  logger.debug('[Portal Debug] No portal data returned for waste provider:', wasteProvider.id);
                }
              } catch (vaultError) {
                logger.debug('[Portal Debug] Error loading portal data for waste provider:', wasteProvider.id, vaultError);
              }
              return wasteProvider;
            })
          );

          logger.debug('[Data Debug] Final waste provider data being set:', updatedData.length, 'providers');
          logger.debug('[Data Debug] First provider account number:', updatedData[0]?.accountNumber);
          setWasteProviderData(updatedData);
        } else {
          setWasteProviderData(data);
        }
      }
    } catch (error) {
      logger.error('Error loading waste provider data:', error);
      Alert.alert('Error', 'Failed to load waste provider information');
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const formatCurrency = (amount: string | number) => {
    if (!amount) return '';
    if (typeof amount === 'number') {
      return `$${amount.toFixed(2)}`;
    }
    return amount.toString();
  };

  const handleEditWasteProvider = (wasteProvider: any) => {
    navigation.navigate('WasteProviderInput', { 
      wasteProviderId: wasteProvider.id, 
      isEdit: true 
    });
  };

  const handleDeleteWasteProvider = async (wasteProvider: any) => {
    Alert.alert('Delete', 'Are you sure you want to delete this waste provider information?', [
      { text: 'Cancel', style: 'cancel' },
      { 
        text: 'Delete', 
        style: 'destructive', 
        onPress: async () => {
          try {
            await wasteProviderInfoService.deleteWasteProviderInfo(wasteProvider.id);
            
            // Reload data after deletion
            await loadWasteProviderData();
            
            Alert.alert('Deleted', 'Waste provider information deleted.');
          } catch (e) {
            logger.error('Delete waste provider info failed:', e);
            Alert.alert('Error', 'Failed to delete waste provider information');
          }
        } 
      },
    ]);
  };

  const handleAddWasteProvider = () => {
    navigation.navigate('WasteProviderInput');
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Waste Provider Information</Text>

        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#29BFC0" />
            <Text style={styles.loadingText}>Loading waste provider information...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  if (!wasteProviderData || wasteProviderData.length === 0) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Waste Provider Information</Text>

        <View style={styles.whiteContainer}>
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>No waste provider information found</Text>
            <TouchableOpacity
              style={styles.addButton}
              onPress={handleAddWasteProvider}
            >
              <Text style={styles.addButtonText}>Add Waste Provider Info</Text>
            </TouchableOpacity>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Waste Provider Information</Text>

      {viewOnly && (
        <View style={styles.viewOnlyBanner}>
          <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
        </View>
      )}

      <View style={styles.whiteContainer}>
        <ScrollView style={styles.scrollView}>
          {wasteProviderData.map((wasteProvider, index) => (
            <View key={wasteProvider.id || index} style={styles.wasteProviderCard}>
              <View style={styles.wasteProviderHeader}>
                <View style={{flex: 1, flexShrink: 1}}>
                  <Text style={styles.wasteProviderName}>
                    {wasteProvider.companyName || 'Unknown Provider'}
                  </Text>
                  <Text style={styles.wasteProviderType}>
                    {wasteProvider.billingCycle || 'Not specified'}
                  </Text>
                </View>
                {!viewOnly && (
                  <View style={styles.cardButtons}>
                    <TouchableOpacity
                      style={styles.editWasteProviderButton}
                      onPress={() => handleEditWasteProvider(wasteProvider)}
                    >
                      <Text style={styles.editWasteProviderButtonText}>Edit</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.deleteWasteProviderButton}
                      onPress={() => handleDeleteWasteProvider(wasteProvider)}
                    >
                      <Text style={styles.deleteWasteProviderButtonText}>Delete</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>
              
              <InfoItem label="Company Name" value={wasteProvider.companyName || ''} />
              
              {wasteProvider.accountNumber && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Account Number 🔒</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>{showAccountNumber[wasteProvider.id] ? wasteProvider.accountNumber : '••••••••••'}</Text>
                    <TouchableOpacity onPress={() => setShowAccountNumber(prev => ({ ...prev, [wasteProvider.id]: !prev[wasteProvider.id] }))} style={styles.eyeButton}>
                      <Image
                        source={showAccountNumber[wasteProvider.id] ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}
              <InfoItem label="Service Address" value={wasteProvider.serviceAddress || ''} />
              <InfoItem label="Billing Address" value={wasteProvider.billingAddress || ''} />
              <InfoItem label="Customer Service Phone" value={formatPhoneNumber(wasteProvider.customerServicePhone) || ''} />
              <InfoItem label="Emergency Phone" value={formatPhoneNumber(wasteProvider.emergencyPhone) || ''} />
              <InfoItem label="Website" value={wasteProvider.website || ''} />
              <InfoItem label="Billing Cycle" value={wasteProvider.billingCycle || ''} />
              <InfoItem label="Monthly Cost" value={formatCurrency(wasteProvider.monthlyCost) || ''} />
              <InfoItem label="Autopay Enabled" value={wasteProvider.autopayEnabled ? 'Yes' : 'No'} />
              <InfoItem label="Autopay Method" value={wasteProvider.autopayMethod || ''} />
              <InfoItem label="Pickup Day" value={wasteProvider.pickupDay || ''} />
              <InfoItem label="Bin Size" value={wasteProvider.binSize || ''} />
              
              {wasteProvider.username && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Portal Username</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>{showUsername[wasteProvider.id] ? wasteProvider.username : '••••••••••'}</Text>
                    <TouchableOpacity onPress={() => setShowUsername(prev => ({ ...prev, [wasteProvider.id]: !prev[wasteProvider.id] }))} style={styles.eyeButton}>
                      <Image
                        source={showUsername[wasteProvider.id] ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}
              
              {wasteProvider.password && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Portal Password</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>{showPassword[wasteProvider.id] ? wasteProvider.password : '••••••••••'}</Text>
                    <TouchableOpacity onPress={() => setShowPassword(prev => ({ ...prev, [wasteProvider.id]: !prev[wasteProvider.id] }))} style={styles.eyeButton}>
                      <Image
                        source={showPassword[wasteProvider.id] ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}
              
              <InfoItem label="Notes" value={wasteProvider.notes || ''} />
            </View>
          ))}

          <View style={styles.buttonContainer}>
            {!viewOnly && (
              <TouchableOpacity
                style={styles.addAnotherWasteProviderButton}
                onPress={handleAddWasteProvider}
              >
                <Text style={styles.addAnotherWasteProviderButtonText}>Add Another Waste Provider</Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={[styles.button, styles.backToListButton]}
              onPress={() => navigation.goBack()}
            >
              <Text style={styles.buttonText}>Back to Checklist</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    marginTop: ms(10),
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  emptyText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: ms(20),
  },
  addButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  wasteProviderCard: {
    backgroundColor: '#F8F9FA',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(20),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  wasteProviderHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: ms(20),
    paddingBottom: ms(15),
    borderBottomWidth: ms(2),
    borderBottomColor: '#29BFC0',
  },
  wasteProviderName: {
    fontSize: ms(20, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(4),
  },
  wasteProviderType: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
  },
  cardButtons: {
    flexDirection: 'row',
    gap: ms(8),
  },
  editWasteProviderButton: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  editWasteProviderButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  deleteWasteProviderButton: {
    backgroundColor: '#DC3545',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  deleteWasteProviderButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  infoItem: {
    marginBottom: ms(15),
  },
  label: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    marginBottom: ms(5),
  },
  value: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  valueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  eyeButton: {
    padding: ms(5),
    marginLeft: ms(10),
  },
  eyeIcon: {
    width: s(16),
    height: s(16),
    tintColor: '#6B7280',
  },
  separator: {
    height: s(1),
    backgroundColor: '#E5E5E5',
  },
  buttonContainer: {
    padding: ms(20),
    paddingBottom: ms(120),
    gap: ms(15),
  },
  addAnotherWasteProviderButton: {
    backgroundColor: '#F2994A',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addAnotherWasteProviderButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  button: {
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});